<template>
  <v-container class="data-grid-page" fluid>
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <!-- Test charge card -->
        <v-card flat class="mb-6">
          <v-card-title class="text-h6">
            <v-icon left color="primary">mdi-credit-card-sync</v-icon>
            MidPay test charge
          </v-card-title>
          <v-card-text>
            <v-alert type="warning" dense border="left" class="mb-4">
              Superuser only. Calls <strong>exactly</strong> the same API as a real member payment
              (<code>POST /schedule/:id/pay</code>). Side effect: marks the schedule as paid.
              Use a dedicated test enrollment/schedule.
            </v-alert>

            <v-alert v-if="resultMessage" :type="resultOk ? 'success' : 'error'" dense class="mb-4" dismissible @input="resultMessage = ''">
              {{ resultMessage }}
            </v-alert>

            <v-text-field
              v-model="scheduleId"
              label="Schedule ID (MongoDB ObjectId)"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
              placeholder="e.g. 664a1f..."
            />

            <v-text-field
              v-model="applicationCode"
              label="Application Code"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
              placeholder="e.g. FHC-12345"
            />

            <v-text-field
              v-model="memberEmail"
              label="Member Email"
              outlined
              dense
              hide-details="auto"
              class="mb-3"
              type="email"
            />

            <v-text-field
              v-model="amountStr"
              label="Amount (USD)"
              type="number"
              step="0.01"
              min="0.01"
              outlined
              dense
              hide-details="auto"
              class="mb-1"
            />
            <p class="text-caption text--secondary mb-4">Must match the schedule's due amount exactly.</p>

            <p class="text-caption text--secondary mb-4">
              Test card: <strong>4111111111111111</strong> (Visa — staging approves, production declines with <em>no issuer found</em>).
              CVV: any · Exp: any future date.
            </p>

            <payment-form
              :amount="numericAmount"
              :user="currentUser"
              :submitting="submitting"
              mode="pay"
              @success="handlePaymentSuccess"
              @error="handlePaymentError"
            />
          </v-card-text>
        </v-card>

        <!-- Smoke test card -->
        <v-card flat outlined>
          <v-card-title class="text-subtitle-1">
            <v-icon left color="teal">mdi-flask-outline</v-icon>
            Post-deploy smoke (no real card)
          </v-card-title>
          <v-card-text>
            <p class="text-body-2 text--secondary mb-3">
              Sends a sale with test Visa <strong>4111111111111111</strong> directly (no CollectJS token).
              <strong>Staging</strong> usually <strong>approves</strong> — that is pass.
              <strong>Production</strong> usually declines with <em>no issuer found</em> — also pass.
              Fails only on payment-token integration errors.
            </p>
            <v-btn color="teal" dark :loading="smokeLoading" @click="runSmoke">
              <v-icon left small>mdi-play</v-icon>
              Run smoke check now
            </v-btn>
            <v-alert v-if="smokeMessage" :type="smokeOk ? 'success' : 'error'" dense text class="mt-3 mb-0">
              {{ smokeMessage }}
            </v-alert>
            <pre v-if="smokeJson" class="text-caption mt-2 pa-2" style="background:#f5f5f5;overflow:auto;max-height:200px;">{{ smokeJson }}</pre>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import PaymentForm from '@/components/memberPayments/PaymentForm.vue';

export default {
  name: 'AdminMidpayTestCharge',
  components: { PaymentForm },
  data() {
    return {
      scheduleId: '',
      applicationCode: '',
      memberEmail: '',
      amountStr: '1',
      submitting: false,
      resultMessage: '',
      resultOk: false,
      smokeLoading: false,
      smokeMessage: '',
      smokeOk: false,
      smokeJson: '',
      currentUser: null,
    };
  },
  computed: {
    numericAmount() {
      const n = parseFloat(String(this.amountStr));
      return Number.isNaN(n) ? 1 : Math.max(0.01, n);
    },
  },
  async mounted() {
    try {
      const res = await axios.get('/api/user');
      this.currentUser = res.data && res.data[0] ? res.data[0] : null;
      if (!this.currentUser || String(this.currentUser.role).toLowerCase() !== 'superuser') {
        this.$router.push('/admin');
      }
    } catch {
      this.$router.push('/login');
    }
  },
  methods: {
    async handlePaymentSuccess(payload) {
      if (!this.scheduleId || !this.applicationCode || !this.memberEmail) {
        this.resultOk = false;
        this.resultMessage = 'Fill in Schedule ID, Application Code, and Member Email before submitting.';
        return;
      }
      this.submitting = true;
      this.resultMessage = '';
      try {
        const { data } = await axios.post('/api/admin/midpay/test-charge', {
          scheduleId: this.scheduleId.trim(),
          applicationCode: this.applicationCode.trim(),
          email: this.memberEmail.trim(),
          paymentToken: payload.paymentToken,
          amount: this.numericAmount,
          billingInfo: payload.billingInfo,
        });
        this.resultOk = data.success !== false;
        const txId = data.transactionId || data.transaction_id || 'N/A';
        const msg = data.message || data.responsetext || (this.resultOk ? 'Approved' : 'Declined');
        this.resultMessage = `${msg} — Transaction ID: ${txId}`;
      } catch (e) {
        this.resultOk = false;
        const d = e.response && e.response.data;
        this.resultMessage = (d && (d.message || d.error || d.details)) || e.message || 'Charge failed';
      } finally {
        this.submitting = false;
      }
    },
    handlePaymentError(msg) {
      this.resultOk = false;
      this.resultMessage = msg || 'Payment error';
    },
    async runSmoke() {
      this.smokeLoading = true;
      this.smokeMessage = '';
      this.smokeJson = '';
      try {
        const { data } = await axios.post('/api/midpay-smoke');
        this.smokeOk = !!data.ok;
        this.smokeMessage = data.logLine || (data.ok ? 'OK' : 'Unexpected — see details');
        this.smokeJson = JSON.stringify(data, null, 2);
      } catch (e) {
        this.smokeOk = false;
        this.smokeMessage = (e.response && e.response.data && e.response.data.error) || e.message || 'Request failed';
        this.smokeJson = e.response && e.response.data ? JSON.stringify(e.response.data, null, 2) : '';
      } finally {
        this.smokeLoading = false;
      }
    },
  },
};
</script>
